import { useRef, useState } from 'react';
import { commentFeed } from '@/mocks/kanliao';

export default function Discussion() {
  const [items, setItems] = useState(commentFeed);
  const [draft, setDraft] = useState('');
  const [posted, setPosted] = useState(false);
  const likedRef = useRef<Set<number>>(new Set());
  const [likes, setLikes] = useState<Record<number, number>>(
    Object.fromEntries(commentFeed.map((c, i) => [i, c.likes])),
  );

  const toggleLike = (i: number) => {
    const active = likedRef.current.has(i);
    setLikes((prev) => {
      const newState = { ...prev };
      newState[i] = active ? (newState[i] ?? 0) - 1 : (newState[i] ?? 0) + 1;
      return newState;
    });
    if (active) likedRef.current.delete(i);
    else likedRef.current.add(i);
  };

  const handlePost = () => {
    if (!draft.trim()) return;
    const newItem = {
      name: '我',
      time: '刚刚',
      text: draft.trim(),
      color: 'bg-orange-200 text-orange-800',
      initial: '我',
      likes: 0,
    };
    setItems((prev) => [newItem, ...prev]);
    setDraft('');
    setPosted(true);
    window.setTimeout(() => setPosted(false), 2200);
  };

  return (
    <section id="discuss" className="bg-gradient-to-b from-amber-50/60 to-white/40 py-12">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.6fr_1fr]">
          <div>
            <span className="text-xs font-semibold tracking-wide text-orange-500">DISCUSSION</span>
            <h2 className="mt-1 text-2xl font-black tracking-tight text-stone-900 md:text-3xl">
              大家都在聊的「看料」入口
            </h2>
            <p className="mt-2 text-sm text-stone-500">
              新收录、失效反馈和好用直达都在这儿，遇到打不开的先来翻翻评论。
            </p>

            <div className="mt-5 rounded-2xl border border-stone-200/80 bg-white/90 p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-500 text-sm font-bold text-white">
                  我
                </span>
                <div className="min-w-0 flex-1">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value.slice(0, 500))}
                    placeholder="分享一个好用入口，或反馈失效地址…"
                    className="w-full resize-none rounded-lg border border-stone-200 bg-amber-50/40 px-3 py-2 text-sm outline-none focus:border-orange-300"
                    rows={2}
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-stone-300">{draft.length}/500</span>
                    <button
                      type="button"
                      onClick={handlePost}
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-orange-600"
                    >
                      <i className="ri-send-plane-line" />
                      发布留言
                    </button>
                  </div>
                  {posted && (
                    <p className="mt-2 text-xs text-emerald-600">已发布（演示效果，未实际提交）</p>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3">
              {items.map((c, i) => (
                <article
                  key={`${c.name}-${i}`}
                  className="rounded-2xl border border-stone-200/80 bg-white/90 p-4"
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${c.color}`}
                    >
                      {c.initial}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-stone-700">{c.name}</span>
                        <span className="whitespace-nowrap text-xs text-stone-300">{c.time}</span>
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-stone-600">{c.text}</p>
                      <div className="mt-2.5 flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggleLike(i)}
                          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-stone-200 px-2.5 py-1 text-xs text-stone-500 transition-colors hover:border-rose-200 hover:text-rose-500"
                        >
                          <i className="ri-heart-3-line" />
                          {likes[i] ?? 0}
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-stone-200 px-2.5 py-1 text-xs text-stone-500 transition-colors hover:border-orange-300 hover:text-orange-500"
                        >
                          <i className="ri-reply-line" />
                          回复
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <aside className="lg:pt-16">
            <div className="rounded-2xl border border-orange-200 bg-orange-50/70 p-5">
              <h3 className="flex items-center gap-2 text-base font-bold text-stone-800">
                <i className="ri-chat-new-line text-orange-500" />
                今日热议话题
              </h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {['怎么快速找回失效入口', '求一个不卡的高清源', '番剧跟更站还活着吗', '短剧合集谁在更新'].map(
                  (t, i) => (
                    <a
                      key={t}
                      href="#discuss"
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-orange-200 bg-white px-3 py-2 text-xs text-stone-600 transition-colors hover:border-orange-400 hover:text-orange-600"
                    >
                      <span className="font-bold text-orange-400">{i + 1}</span>
                      {t}
                    </a>
                  ),
                )}
              </div>
              <p className="mt-4 border-t border-orange-100 pt-3 text-xs leading-relaxed text-stone-400">
                入口失效别急着卸载，评论里常有人分享新地址；这边看到反馈也会尽快补。
              </p>
            </div>

            <div className="mt-4 rounded-2xl border border-stone-200/80 bg-white/90 p-5">
              <h3 className="flex items-center gap-2 text-base font-bold text-stone-800">
                <i className="ri-stack-line text-emerald-500" />
                新收录速报
              </h3>
              <ul className="mt-3 flex flex-col gap-2.5">
                {[
                  ['深夜剧场 · 合集站', '8 分钟前', '热'],
                  ['横屏影视库 · 高清', '26 分钟前', '新'],
                  ['多字幕切换播放页', '1 小时前', '新'],
                ].map(([name, time, tag]) => (
                  <li key={name} className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-sm text-stone-600">
                      <i className="ri-add-box-line text-orange-400" />
                      {name}
                    </span>
                    <span className="flex items-center gap-2 whitespace-nowrap text-xs text-stone-300">
                      {time}
                      <em className="not-italic rounded bg-emerald-100 px-1.5 py-px text-[10px] font-semibold text-emerald-600">
                        {tag}
                      </em>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}